import { useState, useEffect, useCallback } from 'react';
import { axiosInstance as axios, useAuth } from '../context/AuthContext';

const API = '';

export function useSubscription() {
  const { user, loading: authLoading } = useAuth();
  const [subscription, setSubscription] = useState(null);
  const [features, setFeatures] = useState(null);
  const [usage, setUsage] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchSubscriptionData = useCallback(async (attempt = 0) => {
    // Only fetch if user is authenticated
    const token = localStorage.getItem('session_token');
    if (!token) {
      setLoading(false);
      setSubscription({ tier: 'free' });
      setFeatures({ tier: 'free', available: {}, locked: {} });
      return;
    }

    try {
      setLoading(true);
      // Use allSettled so a single hiccupping endpoint doesn't downgrade the user.
      const [subRes, featRes, usageRes] = await Promise.allSettled([
        axios.get(`${API}/api/subscriptions/current`, { withCredentials: true }),
        axios.get(`${API}/api/subscriptions/features`, { withCredentials: true }),
        axios.get(`${API}/api/subscriptions/usage`, { withCredentials: true })
      ]);

      const allFailed = subRes.status === 'rejected' && featRes.status === 'rejected' && usageRes.status === 'rejected';

      if (allFailed) {
        // Treat as transient — retry once after 1.5s before falling back to free.
        if (attempt < 1) {
          console.warn('Subscription fetch failed for all endpoints. Retrying once...');
          setTimeout(() => fetchSubscriptionData(attempt + 1), 1500);
          return;
        }
        // After retry, surface the error but DO NOT silently downgrade if user has a token
        console.error('Subscription fetch failed after retry:', subRes.reason || featRes.reason || usageRes.reason);
        setError(subRes.reason || featRes.reason || usageRes.reason);
        // Keep prior state (don't blast it to free) — user may briefly see a feature locked,
        // but at least we won't claim a paid user is free with stale data.
        setLoading(false);
        return;
      }

      // Apply whatever succeeded — partial data is better than fake "free" data
      if (subRes.status === 'fulfilled') setSubscription(subRes.value.data);
      if (featRes.status === 'fulfilled') setFeatures(featRes.value.data);
      if (usageRes.status === 'fulfilled') setUsage(usageRes.value.data);
      setError(null);
    } catch (err) {
      console.error('Unexpected error in subscription fetch:', err);
      setError(err);
    } finally {
      setLoading(false);
    }
  }, []);

  // Wait for auth to be ready before fetching subscription
  useEffect(() => {
    if (!authLoading && user) {
      fetchSubscriptionData();
    } else if (!authLoading && !user) {
      // Not logged in - set free tier
      setLoading(false);
      setSubscription({ tier: 'free' });
      setFeatures({ tier: 'free', available: {}, locked: {} });
    }
  }, [authLoading, user, fetchSubscriptionData]);

  const hasFeature = useCallback((feature) => {
    if (!features) return false;
    return features.available?.[feature] === true;
  }, [features]);

  const getRequiredTier = useCallback((feature) => {
    if (!features) return 'seedling';
    return features.locked?.[feature] || 'free';
  }, [features]);

  const canCreate = useCallback((resourceType) => {
    if (!usage) return true;
    const resource = usage.usage?.[resourceType];
    if (!resource) return true;
    if (resource.unlimited) return true;
    return resource.used < resource.limit;
  }, [usage]);

  const getUsageInfo = useCallback((resourceType) => {
    if (!usage) return { used: 0, limit: 0, unlimited: false };
    return usage.usage?.[resourceType] || { used: 0, limit: 0, unlimited: false };
  }, [usage]);

  return {
    subscription,
    features,
    usage,
    loading,
    error,
    hasFeature,
    getRequiredTier,
    canCreate,
    getUsageInfo,
    refresh: fetchSubscriptionData,
    tier: subscription?.tier || 'free',
    tierName: subscription?.tier_info?.name || 'Free'
  };
}

// Feature constants for easy reference
export const FEATURES = {
  // Seedling tier
  SPIRITUAL_FEATURES: 'spiritual_features',
  PRAYER_REQUESTS: 'prayer_requests',
  DEVOTIONALS: 'devotionals',
  ASSIGN_ROLES: 'assign_roles',
  EDIT_TIMELINE: 'edit_timeline',
  EMAIL_NOTIFICATIONS: 'email_notifications',
  INVITE_PROXY_MEMBERS: 'invite_proxy_members',
  SHARE_ALBUMS: 'share_albums',
  EVENT_TASKS: 'event_tasks',
  
  // Growing tier
  LEGAL_VAULT: 'legal_vault',
  DUES_MANAGEMENT: 'dues_management',
  VOTING_GOVERNANCE: 'voting_governance',
  SHARE_ALBUMS_DOWNLOAD: 'share_albums_download',
  EVENT_BUDGET: 'event_budget',
  PRIORITY_MESSAGES: 'priority_messages',
  
  // Flourishing tier
  RESOURCES_PROFESSIONALS: 'resources_professionals',
  FAMILY_ANALYTICS: 'family_analytics',
  ACHIEVEMENTS_BADGES: 'achievements_badges',
  EXPORT_DATA: 'export_data',
  INVITATION_APPROVAL: 'invitation_approval',
};
